import type { ReactNode } from "react";
import { Check } from "lucide-react";
import { useCurrentUserContext } from "@/hooks/useUser.tsx";
import { AvatarWithFallback } from "@/components/ui/avatar.tsx";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx";
import { cn } from "@/lib/utils.ts";

export type FromOption = {
  handleId: string;
  handleValue: string;
  handleUrn: string;
  handleType: "emailAddress" | "phoneNumber" | "address";
  contactId: string;
  contactName: string;
  contactPicture?: string;
  isAssistant: boolean;
};

/**
 * Resolves the two sender choices from the current user context: the user
 * themselves and their assistant. Returns undefined for either side when the
 * corresponding email handle isn't present, so callers can degrade to a
 * non-clickable display.
 */
export const useEmailFromOptions = (): {
  userOption?: FromOption;
  assistantOption?: FromOption;
} => {
  const { userContext } = useCurrentUserContext();
  const userContact = userContext?.user.userContact;
  const assistantContact = userContext?.user.assistant;

  const userEmailHandle =
    userContact?.handles?.find((h) => h.type === "emailAddress" && h.isDefault) ??
    userContact?.handles?.find((h) => h.type === "emailAddress");
  // Pin the assistant option to the server-resolved canonical address;
  // assistantCanonicalEmail is authoritative for outbound `From:` headers.
  const assistantCanonicalEmail = userContext?.user.assistantCanonicalEmail;
  const assistantEmailHandle = assistantCanonicalEmail
    ? assistantContact?.handles?.find(
        (h) => h.type === "emailAddress" && h.value === assistantCanonicalEmail,
      )
    : undefined;

  const userOption: FromOption | undefined =
    userContact && userEmailHandle
      ? {
          handleId: userEmailHandle.id,
          handleValue: userEmailHandle.value,
          handleUrn: userEmailHandle.urn,
          handleType: "emailAddress",
          contactId: userContact.id,
          contactName: userContact.name,
          contactPicture: userContact.picture ?? undefined,
          isAssistant: false,
        }
      : undefined;

  const assistantOption: FromOption | undefined =
    assistantContact && assistantEmailHandle
      ? {
          handleId: assistantEmailHandle.id,
          handleValue: assistantEmailHandle.value,
          handleUrn: assistantEmailHandle.urn,
          handleType: "emailAddress",
          contactId: assistantContact.id,
          contactName: assistantContact.name,
          contactPicture: assistantContact.picture ?? undefined,
          isAssistant: true,
        }
      : undefined;

  return { userOption, assistantOption };
};

/**
 * Click-to-open menu that lets the user pick the email sender between
 * themselves and their assistant. The send path in send_email_draft.ts
 * derives the "is from assistant?" routing decision from the contact ent
 * type behind the chosen handle, so flipping the selection here is what
 * actually routes the outbound through PostMark vs the user's Gmail or
 * Outlook connector.
 *
 * Renders just `trigger` unchanged when either side is missing — the parent
 * stays responsible for the surrounding layout. The caller is expected to
 * pass a pill with `disableContactSidebar` so clicks don't also open the
 * contact-detail sidebar.
 */
export const EmailFromPickerPopover = ({
  trigger,
  selectedHandleId,
  onSelect,
}: {
  trigger: ReactNode;
  selectedHandleId: string;
  onSelect: (option: FromOption) => Promise<unknown> | undefined;
}) => {
  const { userOption, assistantOption } = useEmailFromOptions();

  if (!userOption || !assistantOption) {
    return <>{trigger}</>;
  }

  const options = [
    { option: userOption, suffix: "(you)" },
    { option: assistantOption, suffix: "(assistant)" },
  ];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Choose sender"
        data-statsig-name="artifact-email-from-picker"
        className="cursor-pointer rounded-full outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
      >
        {trigger}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72">
        <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
          Send as
        </DropdownMenuLabel>
        {options.map(({ option, suffix }) => {
          const isSelected = option.handleId === selectedHandleId;
          return (
            <DropdownMenuItem
              key={option.handleId}
              className={cn("gap-2", isSelected && "bg-accent/40")}
              onSelect={async () => {
                if (!isSelected) {
                  await onSelect(option);
                }
              }}
            >
              <AvatarWithFallback
                size="sm"
                person={{
                  name: option.contactName,
                  picture: option.contactPicture,
                  value: option.handleValue,
                }}
              />
              <div className="flex-1 min-w-0">
                <div className="truncate font-medium">
                  {option.contactName}{" "}
                  <span className="text-muted-foreground font-normal">{suffix}</span>
                </div>
                <div className="truncate text-xs text-muted-foreground">{option.handleValue}</div>
              </div>
              {isSelected && <Check className="size-4 shrink-0 text-muted-foreground" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
